import type { ActionBindingMap } from './keyboardBindingMap';
import {
  PAGE_ACTIONS,
  SLOT_ACTIONS,
  parsePageAction,
  parseSlotAction,
  type PageFlip,
  type SlotPress,
} from '../../../shared/hotbar/slotActions';
import {
  MODIFIER_KEY_CODES,
  type ModifierFamily,
  type ModifierEventFlags,
  reconcileModifierKeys,
  isModifierSatisfied,
  getModifierFamilyFromCode,
  isGameplayAction,
} from './keyboardModifierState';

export {
  MODIFIER_KEY_CODES,
  reconcileModifierKeys,
  isModifierSatisfied,
};
export type {
  ModifierFamily,
  ModifierEventFlags,
};

/**
 * Continuous movement & jump actions that should remain responsive while holding
 * Shift / Ctrl / Alt for ability or targeting chords (e.g., Shift+Z, Shift+X,
 * Shift+1), unless an explicit chord on the movement key itself (such as Ctrl+S
 * for Sit/Rest) is satisfied.
 */
export const MOVEMENT_ACTION_NAMES: ReadonlySet<string> = new Set([
  'move_left',
  'move_right',
  'move_up',
  'move_down',
  'jump',
  'jump_alt',
]);

/**
 * Checks whether an explicit chord exists for `code` across other actions
 * that is currently satisfied by `keysDown`.
 * If an explicit chord on this base key is satisfied, an unmodified binding
 * on the same key must be suppressed.
 */
export function hasSatisfiedExplicitChord(
  actionToCodes: ActionBindingMap,
  code: string,
  keysDown: ReadonlySet<string>,
  currentAction?: string,
  consumedModifier?: ModifierFamily | null
): boolean {
  for (const [actionName, bindings] of actionToCodes.entries()) {
    if (currentAction && actionName === currentAction) continue;
    for (const binding of bindings) {
      if (binding.code === code && binding.modifier) {
        if (isModifierSatisfied(binding.modifier, keysDown, consumedModifier)) {
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * Checks whether an action is active (held down) given the current action mappings,
 * the set of held DOM key codes, and an optional consumed modifier.
 */
export function isActionDown(
  actionToCodes: ActionBindingMap,
  action: string,
  keysDown: ReadonlySet<string>,
  consumedModifier?: ModifierFamily | null
): boolean {
  const config = actionToCodes.get(action);
  if (!config) return false;

  const effectiveConsumed = isGameplayAction(action) ? consumedModifier : null;

  return config.some(item => {
    const keyIsDown = keysDown.has(item.code);
    if (!keyIsDown) return false;

    // Explicit chord precedence: an unmodified binding on `item.code` is suppressed
    // if an explicit chord on the same base key is satisfied.
    if (!item.modifier && hasSatisfiedExplicitChord(actionToCodes, item.code, keysDown, action, consumedModifier)) {
      return false;
    }

    // Continuous movement / jump bindings stay active while modifiers (Shift/Ctrl/Alt)
    // are held for ability chords (e.g. Shift+Z / Shift+X), as long as no explicit chord
    // on this movement key itself is satisfied above.
    if (!item.modifier && MOVEMENT_ACTION_NAMES.has(action)) {
      return true;
    }

    const keyMod = getModifierFamilyFromCode(item.code);
    const modToIgnore = keyMod ?? effectiveConsumed;

    return isModifierSatisfied(item.modifier, keysDown, modToIgnore);
  });
}

/**
 * Checks whether an action was triggered (just pressed this frame) given the current
 * action mappings, the set of keys pressed this frame, the set of held keys,
 * and an optional consumed modifier.
 */
export function isActionJustPressed(
  actionToCodes: ActionBindingMap,
  action: string,
  keysJustPressed: ReadonlySet<string>,
  keysDown: ReadonlySet<string>,
  consumedModifier?: ModifierFamily | null
): boolean {
  const config = actionToCodes.get(action);
  if (!config) return false;

  const effectiveConsumed = isGameplayAction(action) ? consumedModifier : null;

  return config.some(item => {
    const keyJustPressed = keysJustPressed.has(item.code);
    if (!keyJustPressed) return false;

    // Explicit chord precedence: an unmodified binding on `item.code` is suppressed
    // if an explicit chord on the same base key is satisfied.
    if (!item.modifier && hasSatisfiedExplicitChord(actionToCodes, item.code, keysDown, action, consumedModifier)) {
      return false;
    }

    if (!item.modifier && MOVEMENT_ACTION_NAMES.has(action)) {
      return true;
    }

    const keyMod = getModifierFamilyFromCode(item.code);
    const modToIgnore = keyMod ?? effectiveConsumed;

    return isModifierSatisfied(item.modifier, keysDown, modToIgnore);
  });
}

/**
 * Resolves keyboard hotbar slot activation (modular bars plan, phase 3): the
 * first `bar<B>_slot<S>` action pressed this frame, bar 1 first. The slot fires
 * on that bar's current page; PlayerManager.performAbilityFromSlot reads the
 * LIVE bar, so whatever the player dragged into the slot is what fires.
 */
export function resolveKeyboardAbilitySlot(
  isJustPressed: (action: string) => boolean
): SlotPress | undefined {
  for (const action of SLOT_ACTIONS) {
    if (isJustPressed(action)) return parseSlotAction(action) ?? undefined;
  }
  return undefined;
}

/** The first `bar<B>_page_*` action pressed this frame (all unbound by default, D5). */
export function resolveKeyboardPageFlip(
  isJustPressed: (action: string) => boolean
): PageFlip | undefined {
  for (const action of PAGE_ACTIONS) {
    if (isJustPressed(action)) return parsePageAction(action) ?? undefined;
  }
  return undefined;
}

export const UI_ACTION_NAMES = [
  'inventory',
  'character_sheet',
  'skills',
  'quest_log',
  'map',
  'fishing_cast',
  'fishing_reel',
] as const;

export const TARGETING_ACTION_NAMES = [
  'target_next',
  'target_prev',
  'target_nearest',
  'clear_target',
  'toggle_autoattack',
] as const;

/**
 * Dispatches active UI and targeting actions to the supplied onUI callback.
 */
export function dispatchUIAndTargetingActions(
  isJustPressed: (action: string) => boolean,
  onUI: (action: string) => void
): void {
  for (const action of UI_ACTION_NAMES) {
    if (isJustPressed(action)) {
      onUI(action);
    }
  }
  for (const action of TARGETING_ACTION_NAMES) {
    if (isJustPressed(action)) {
      onUI(action);
    }
  }
}
